/**
 * Map tuning (docs/01 room kinds, docs/04 environment): ADVANCE route
 * geometry, gate timing + failsafes, boss chamber sizes, decor density,
 * static bake/cull and ambient motes. Theme palettes: config/themes.ts.
 */
export const MAPS = {
  route: {
    /** Gap between two pieces' outer rects (bulkhead = wall + gap + wall) */
    bulkhead: 96,
    /** Walkable doorway width across the bulkhead */
    doorWidth: 640,
    /** How far a doorway reaches into each piece's walkable rect */
    doorJoin: 140,
    /** Gate barrier thickness (along the door axis) */
    gateThickness: 48,
    /** Gate box overhang past the doorway sides (no corner leaks) */
    gateOverhang: 28,
    /** A piece's fight triggers once the ship is this deep past its entry */
    triggerDepth: 340,
    /**
     * Segment spawn frame: arena formations (u along travel) squeezed to
     * u0 + u·span so squads stand ~as far from the ship as in an arena
     */
    segmentFrame: { u0: 0.02, span: 0.68 },
    /** Held boss parks here in a segment's spawn frame (drone squads orbit it) */
    holdAnchor: { u: 0.82, v: 0.5 },
    /** Past the doorway the waypoint sits this many trigger depths deep */
    waypointDepth: 1.15,
  },

  /** Spawn search (Arena.clearPoint): rings around the wanted spot */
  spawnSearch: { step: 80, max: 1400, angle: Math.PI / 8 },
  /** Clearance of the ship's spawn / rescue point off obstacles */
  spawnClear: 60,

  gates: {
    /** Dissolve animation after a segment clears (s) */
    dissolve: 0.9,
    /** Re-seal animation behind the ship (s) */
    seal: 0.35,
    /** Shake + spark burst on the dissolve */
    openShake: 5,
    openSparks: 7,
    sparkScale: 0.5,
  },

  /** Advance-room failsafes — the run can never soft-lock (docs/01) */
  failsafe: {
    /** Segment waves done + no enemy left this long → the gate forces open */
    gateStall: 4,
    /** Ship outside every walkable/unlocked piece this long → rescued to the entry */
    lostShip: 1.5,
    /** Pause between the room start and segment 1's first wave (s) */
    firstWaveDelay: 1.2,
  },

  /** Boss chambers (landscape). Bosses that roam wide get the large one. */
  chamber: {
    normal: { w: 2500, h: 1700 },
    large: { w: 2900, h: 1950 },
    largeBosses: [
      'serpent', 'centipede', 'broodworm', 'kraken', 'gale', 'admiral', 'carrier', 'webspinner',
      'stalker', 'splitter', 'quartet',
    ],
  },

  /** Static decor (docs/04): densities per million px² of walkable floor */
  decor: {
    decalsPerMpx: 9,
    /** Wall dressing props per 1000 px of wall edge */
    propsPerKpx: 1.6,
    /** Decals keep this far off obstacles and doorways */
    decalClear: 40,
    decalScale: [0.7, 1.25],
    propScale: [0.75, 1.15],
    /** Props sit this far into the wall band (fraction of wall thickness) */
    propInset: 0.35,
    /** Wall blob spacing along every walkable edge */
    wallSpacing: 96,
    landmarkSize: 384,
  },

  /**
   * Static decor lives in spatial cells culled by the camera; each cell's
   * floor decals are BAKED into one cached texture (half resolution: a
   * 832 px cell + decal overhang fits a 512² texture, 1 MB per cell).
   */
  bake: {
    chunk: 832,
    resolution: 0.5,
    /** Chunks / ground pieces within this margin of the view stay visible */
    cullMargin: 160,
  },

  /** Objective arrow (ui/objective-arrow.ts) — screen px */
  arrow: {
    /** Edge insets — the top one clears the boss bar + wave label band */
    margin: 72,
    marginTop: 160,
    scale: 0.62,
    iconScale: 0.5,
    /** Icon sits this far behind the arrow tip (toward the screen centre) */
    iconGap: 46,
    pulse: 0.08,
    pulseHz: 1.4,
    /** On-screen waypoint marker: lift above the spot + bob */
    markerLift: 76,
    bob: 8,
    bobHz: 1.2,
  },

  /** Ambient motes (sparks / embers / spores / glitch) around the camera */
  motes: {
    count: 46,
    /** Spawn band around the view (px) — motes wrap inside it */
    margin: 120,
    alpha: [0.25, 0.75],
    scale: [0.5, 1.1],
  },
} as const;

export type ChamberSize = { readonly w: number; readonly h: number };
